import { useLazyQuery } from '@apollo/client';
import { IonRouterLink, IonText } from '@ionic/react';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import DateAndTimeStamp from '@components/dates/DateAndTimeStamp';
import styles from '@components/expert/ExpertHighlightsTable.module.css';
import { Filters } from '@components/forms/ExpertKPIsFilters';
import HandleQuery from '@components/HandleQuery';
import Nbsp from '@components/Nbsp';
import Table, { TableHeader } from '@components/Table';
import Icon from '@components/text/Icon';
import Hyperlink from '@components/typography/Hyperlink';
import useMyAdmin from '@hooks/useMyAdmin';
import useMyExpert from '@hooks/useMyExpert';
import useNodesFromConnection from '@hooks/useNodesFromConnection';
import {
  ExpertGlobalRoleEnum,
  ExpertOverviewsSortByEnum,
  GetExpertOverviewsDocument,
  GetExpertOverviewsQuery,
  GetExpertOverviewsQueryVariables,
  SortDirectionEnum
} from '@typing/Generated';
import { calculateDatesFromTimePeriod, sortByTimestamp } from '@utils/modelUtils';
import { minutesToHoursAndMinutes } from '@utils/timeUtils';

type OutOfOfficePeriodType = NonNullable<
  NonNullable<
    NonNullable<NonNullable<NonNullable<GetExpertOverviewsQuery['expertOverviews']['edges']>[number]>['node']>['expert']
  >['futureOoops']
>[number];

type Props = {
  filters: Filters;
};

const ExpertHighlightsTable = ({ filters }: Props) => {
  const intl = useIntl();
  const myAdmin = useMyAdmin();
  const myExpert = useMyExpert();

  const [getExpertOverviews, getExperyOverviewsQuery] = useLazyQuery<
    GetExpertOverviewsQuery,
    GetExpertOverviewsQueryVariables
  >(GetExpertOverviewsDocument);

  const [sortBy, setSortBy] = useState<ExpertOverviewsSortByEnum>(ExpertOverviewsSortByEnum.name);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.descending);

  useEffect(() => {
    if (myAdmin || myExpert) {
      const dates = calculateDatesFromTimePeriod(filters.timePeriod, filters.startDate, filters.endDate, intl);

      getExpertOverviews({
        variables: {
          endDate: dates.endDate,
          expertId:
            filters.expertId === myExpert?.id && myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager
              ? null
              : filters.expertId,
          sortBy,
          sortDirection,
          startDate: dates.startDate
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, getExpertOverviews, myExpert, sortBy, sortDirection]);

  const overviews = useNodesFromConnection(getExperyOverviewsQuery.data?.expertOverviews);

  const totals = useMemo(
    () => ({
      consultsToday: overviews.reduce((count, current) => count + (current.expert?.summary?.consultsToday ?? 0), 0),
      initial: overviews.reduce((count, current) => count + current.initialConsults, 0),
      newClient: overviews.reduce((count, current) => count + (current.expert?.summary?.newClients ?? 0), 0),
      rest: overviews.reduce((count, current) => count + current.thirdPlusConsults, 0),
      second: overviews.reduce((count, current) => count + current.secondConsults, 0),
      turnAroundTime: overviews.reduce((count, current) => count + current.carePlanTurnAroundTime, 0),
      unreadMessages: overviews.reduce((count, current) => count + (current.expert?.summary?.unreadMessages ?? 0), 0)
    }),
    [overviews]
  );

  const tableHeaders: TableHeader<ExpertOverviewsSortByEnum>[] = [
    { columnSortBy: ExpertOverviewsSortByEnum.name },
    {
      columnSortBy: ExpertOverviewsSortByEnum.unread_messages,
      messageValues: { count: totals.unreadMessages }
    },
    { columnSortBy: ExpertOverviewsSortByEnum.consults_today, messageValues: { count: totals.consultsToday } },
    { messageKey: 'pages.expert.highlights.tableHeadings.newClients', messageValues: { count: totals.newClient } },
    { messageKey: 'pages.expert.highlights.tableHeadings.initialConsult', messageValues: { count: totals.initial } },
    { messageKey: 'pages.expert.highlights.tableHeadings.secondConsult', messageValues: { count: totals.second } },
    { messageKey: 'pages.expert.highlights.tableHeadings.restConsult', messageValues: { count: totals.rest } },
    { messageKey: 'pages.expert.highlights.tableHeadings.turnAroundTime' },
    { messageKey: 'pages.expert.highlights.tableHeadings.avgRefreshTime' },
    { messageKey: 'pages.expert.highlights.tableHeadings.timeTracking' },
    { messageKey: 'pages.expert.highlights.tableHeadings.outOfOffice' }
  ];

  return (
    <HandleQuery query={getExperyOverviewsQuery}>
      {expertsData => (
        <>
          <Table<ExpertOverviewsSortByEnum>
            headerKey="pages.expert.highlights.tableHeadings"
            headers={tableHeaders}
            pageInfo={expertsData?.expertOverviews.pageInfo}
            setSortBy={setSortBy}
            setSortDirection={setSortDirection}
            skipScroll
            skipSpacing
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            {overviews.map(overview => {
              const firstOOOP = sortByTimestamp<OutOfOfficePeriodType>(
                overview.expert?.futureOoops ?? [],
                ooop => ooop.startTime
              ).reverse()?.[0];

              const consults = {
                initial: overview.initialConsults,
                rest: overview.thirdPlusConsults,
                second: overview.secondConsults
              };

              const timeTrackingPercentages = {
                billable:
                  overview.activityKindBillableMinutes.total /
                  (overview.activityKindBillableMinutes.total + overview.activityKindNonBillableMinutes.total),
                nonBillable:
                  overview.activityKindNonBillableMinutes.total /
                  (overview.activityKindBillableMinutes.total + overview.activityKindNonBillableMinutes.total)
              };

              return (
                <tr key={overview.expert?.id}>
                  <td>
                    <Hyperlink
                      routerLink={
                        myExpert?.id === overview.expert?.id
                          ? '/expert/journeys'
                          : myAdmin
                          ? `/admin/experts/${overview.expert?.id ?? ''}`
                          : `/expert/manage-experts/${overview.expert?.id ?? ''}/journeys`
                      }
                    >
                      {overview.expert?.fullName}
                    </Hyperlink>
                  </td>
                  <td>
                    <div className={styles.unreadMessageWrapper}>
                      <IonRouterLink
                        className={myAdmin ? styles.disabled : undefined}
                        routerLink={
                          myExpert?.id === overview.expert?.id
                            ? '/expert/journeys'
                            : myAdmin
                            ? undefined
                            : `/expert/manage-experts/${overview.expert?.id ?? ''}/journeys`
                        }
                      >
                        <Icon ariaLabelKey="accessibility.ariaLabels.comments" icon="comments" />
                        {overview.expert?.summary?.unreadMessages !== undefined &&
                          overview.expert?.summary?.unreadMessages > 0 && (
                            <div className={styles.unreadMessageBadge}>
                              <FormattedNumber value={overview.expert?.summary?.unreadMessages} />
                            </div>
                          )}
                      </IonRouterLink>
                    </div>
                  </td>
                  <td>
                    <div className={styles.unreadMessageWrapper}>
                      <IonRouterLink
                        className={myAdmin ? styles.disabled : undefined}
                        routerLink={myAdmin ? undefined : `/expert/consults?expertId=${overview.expert?.id ?? ''}`}
                      >
                        <Icon ariaLabelKey="accessibility.ariaLabels.calendar" icon="calendar" />
                        {overview.expert?.summary?.consultsToday !== undefined &&
                          overview.expert?.summary?.consultsToday > 0 && (
                            <div className={styles.unreadMessageBadge}>{overview.expert?.summary?.consultsToday}</div>
                          )}
                      </IonRouterLink>
                    </div>
                  </td>
                  <td>
                    <div className={styles.unreadMessageWrapper}>
                      <IonRouterLink
                        className={myAdmin ? styles.disabled : undefined}
                        routerLink={
                          myExpert?.id === overview.expert?.id
                            ? '/expert/journeys'
                            : myAdmin
                            ? undefined
                            : `/expert/manage-experts/${overview.expert?.id ?? ''}/journeys`
                        }
                      >
                        <Icon ariaLabelKey="accessibility.ariaLabels.users" icon="users" />
                        {overview.expert?.summary?.newClients !== undefined &&
                          overview.expert?.summary?.newClients > 0 && (
                            <div className={styles.unreadMessageBadge}>{overview.expert?.summary?.newClients}</div>
                          )}
                      </IonRouterLink>
                    </div>
                  </td>
                  <td>
                    <div className={styles.circle + ' ' + styles.light}>{consults.initial}</div>
                  </td>
                  <td>
                    <div className={styles.circle + ' ' + styles.teal}>{consults.second}</div>
                  </td>
                  <td>
                    <div className={styles.circle + ' ' + styles.purple}>{consults.rest}</div>
                  </td>
                  <td>
                    {minutesToHoursAndMinutes({
                      minutes: (overview.carePlanTurnAroundTime ?? 0) / 60,
                      roundRemainder: true
                    })}
                    <Nbsp />
                    <FormattedMessage id="pages.expert.dashboard.overview.hours" />
                  </td>
                  <td>
                    {(overview.averageRefreshTime / 60 / 60 / 24).toFixed(2)}
                    <Nbsp />
                    <FormattedMessage id="pages.expert.dashboard.overview.days" />
                  </td>
                  <td>
                    <div>
                      <FormattedMessage
                        id="pages.expert.highlights.billable"
                        values={{
                          value: isNaN(timeTrackingPercentages.billable)
                            ? 0
                            : (timeTrackingPercentages.billable * 100).toFixed(2)
                        }}
                      />
                    </div>
                    <div>
                      <FormattedMessage
                        id="pages.expert.highlights.nonBillable"
                        values={{
                          value: isNaN(timeTrackingPercentages.nonBillable)
                            ? 0
                            : (timeTrackingPercentages.nonBillable * 100).toFixed(2)
                        }}
                      />
                    </div>
                  </td>
                  <td>
                    {firstOOOP && (
                      <IonText className={styles.ooo}>
                        <DateAndTimeStamp showWeekday={false} value={firstOOOP.startTime} />
                      </IonText>
                    )}
                  </td>
                </tr>
              );
            })}
          </Table>
        </>
      )}
    </HandleQuery>
  );
};

export default ExpertHighlightsTable;
